import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isAssemblyKeytermsEnabled } from '@helpers/featureFlags/isAssemblyKeytermsEnabled';
import { useAuthContext } from '@providers/AuthProvider';
import { NoticeExtractorService, NoticeExtractorWorkflowStatusResponseDto } from 'app/API';
import { getNoticeExtractorErrorMessage } from '../../helpers/getNoticeExtractorErrorMessage';
import { ResultView } from './components/ResultView';
import { finalStates } from './constants/finalStates';

export const Content = () => {
  const { organization } = useAuthContext();
  const [searchParams] = useSearchParams();
  const queryWorkflowId = searchParams.get('workflowId') ?? '';
  const loadedQueryWorkflowIdRef = useRef('');
  const [jobId, setJobId] = useState(queryWorkflowId);
  const [workflowStatus, setWorkflowStatus] = useState<NoticeExtractorWorkflowStatusResponseDto | undefined>();
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const activeJobId = workflowStatus?.jobId || jobId;
  const shouldPoll = activeJobId && workflowStatus?.state && !finalStates.has(workflowStatus.state);
  const showKeyterms = isAssemblyKeytermsEnabled(organization?.featureFlags);

  const loadStatus = useCallback(async (nextJobId: string) => {
    if (!nextJobId) {
      return;
    }

    try {
      setIsCheckingStatus(true);
      setErrorMessage('');
      const status = await NoticeExtractorService.getExtractionJobStatus({ jobId: nextJobId });
      setWorkflowStatus(status.body);
      setJobId(status.body.jobId);
    } catch (error) {
      setErrorMessage(getNoticeExtractorErrorMessage(error));
    } finally {
      setIsCheckingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void loadStatus(activeJobId);
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeJobId, loadStatus, shouldPoll]);

  useEffect(() => {
    if (!queryWorkflowId || loadedQueryWorkflowIdRef.current === queryWorkflowId) {
      return;
    }

    loadedQueryWorkflowIdRef.current = queryWorkflowId;
    setJobId(queryWorkflowId);
    void loadStatus(queryWorkflowId);
  }, [loadStatus, queryWorkflowId]);

  return (
    <main className="min-h-[calc(100vh-120px)] px-[30px] pb-[50px] pt-5 text-foreground">
      <div>
        <section className="min-h-[520px] rounded-xl border border-border bg-surface p-[18px] text-foreground shadow-[0_8px_10px_var(--color-shadow)]">
          {errorMessage ? <div className="mt-2 text-xs leading-[18px] text-danger">{errorMessage}</div> : null}
          {workflowStatus ? (
            <>
              <div className="mb-4 grid grid-cols-3 gap-2.5 max-[860px]:grid-cols-1">
                <div className="min-w-0 rounded-[10px] border border-border bg-surface-muted p-3">
                  <span className="block text-[11px] font-bold uppercase leading-[14px] text-foreground-muted">
                    Workflow id
                  </span>
                  <span className="mt-1 block [overflow-wrap:anywhere] text-sm font-bold leading-5 text-foreground">
                    {workflowStatus.jobId}
                  </span>
                </div>
                <div className="min-w-0 rounded-[10px] border border-border bg-surface-muted p-3">
                  <span className="block text-[11px] font-bold uppercase leading-[14px] text-foreground-muted">
                    State
                  </span>
                  <span className="mt-1 block [overflow-wrap:anywhere] text-sm font-bold leading-5 text-foreground">
                    {workflowStatus.state}
                  </span>
                </div>
                <div className="min-w-0 rounded-[10px] border border-border bg-surface-muted p-3">
                  <span className="block text-[11px] font-bold uppercase leading-[14px] text-foreground-muted">
                    Status check
                  </span>
                  <span className="mt-1 block [overflow-wrap:anywhere] text-sm font-bold leading-5 text-foreground">
                    {isCheckingStatus ? 'Refreshing' : 'Idle'}
                  </span>
                </div>
              </div>
              {workflowStatus.failedReason ? (
                <div className="mt-2 text-xs leading-[18px] text-danger">{workflowStatus.failedReason}</div>
              ) : null}
              {workflowStatus.result ? (
                <ResultView result={workflowStatus.result} showKeyterms={showKeyterms} />
              ) : (
                <div className="flex min-h-[260px] items-center justify-center rounded-[10px] border border-dashed border-border bg-surface-muted p-6 text-center text-sm leading-[18px] text-foreground-muted">
                  Workflow is running. Result will appear here when Temporal completes it.
                </div>
              )}
            </>
          ) : (
            <div className="flex min-h-[260px] flex-col items-center justify-center gap-3 rounded-[10px] border border-dashed border-border bg-surface-muted p-6 text-center text-sm leading-[18px] text-foreground-muted">
              {jobId ? (
                <>
                  <span className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-foreground-muted" />
                  <span>Loading notice extraction result.</span>
                </>
              ) : (
                'Open this page from a job notice attachment.'
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};
